import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import moment from 'moment';
import i18n from 'i18n';
import styles from './orderDetailContent.module.scss';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleUp, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { AddonFeatureManager, AuthenticationManager } from 'core';
import { ReportType } from 'core/report/ReportData';
import { hasFuncs } from 'core/permission/PermissionDSL';
import { Permission } from 'core/auth/Permission';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { Order } from 'core/order/Order';
import { ButtonWithConfirmation } from 'components/ButtonWithWarning/ButtonWithWarning';
import {
  useCheckRemainingStoredValue,
  useSettle,
  useApprove,
  useReject,
  useEditOrder,
} from './OrderDetailContentHooks';

import OrderDetailInfo from '../OrderDetailInfo/OrderDetailInfo';

const cssClassNames = classNames.bind(styles);

interface OrderDetailContentType {
  order: Order;
  refetchOrder: () => void;
  authenticationManager: AuthenticationManager;
  addonFeatureManager: AddonFeatureManager;
}
const ApproveButton = ({ onClick = () => {}, disabled = false }) => {
  const className = cssClassNames('approveBtn', { disabled });
  return (
    <div className={className} onClick={onClick}>
      {i18n.t<string>('orderDetail.labels.approve')}
    </div>
  );
};

const RejectButton = ({ onClick = () => {} }) => {
  return (
    <div className={styles.rejectBtn} onClick={onClick}>
      {i18n.t<string>('orderDetail.labels.reject')}
    </div>
  );
};

const SettleButton = ({
  disabled = false,
  settleTips = '',
  onSettle = () => {},
}) => {
  const settleClass = cssClassNames('settleBtn', { disabled });
  const title = (
    <>
      {i18n.t<string>('orderDetail.buttons.settle')}
      <span className={styles.settleTips}>{settleTips}</span>
    </>
  );
  return (
    <ButtonWithConfirmation
      title={title}
      className={settleClass}
      onAction={onSettle}
      description={i18n.t<string>(
        'orderDetail.labels.SettlementConfirmationQuestions',
      )}
    />
  );
};

const getReportLink = (order: Order, type: ReportType): string => {
  const from = encodeURIComponent(
    moment(order.startDate).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
  );
  const to = encodeURIComponent(
    moment(order.endDate).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
  );
  return `/reports/performance?type=${type}&dimension=adsOrderId&from=${from}&to=${to}&adsOrderId=${order.id}`;
};
const OrderDetailContent: FC<OrderDetailContentType> = ({
  order,
  refetchOrder,
  authenticationManager,
  addonFeatureManager,
}) => {
  const [showDetail, setShowDetail] = useState(true);
  const toggleShowDetail = () => setShowDetail((v) => !v);

  const { remainingStoredValueError } = useCheckRemainingStoredValue(
    order,
    addonFeatureManager,
  );
  const { showApproveButton, approveLoading, approveOrder } = useApprove(
    order,
    refetchOrder,
  );
  const { showRejectButton, rejectLoading, rejectOrder } = useReject(
    order,
    refetchOrder,
  );
  const {
    canSettle,
    settleTips,
    showSettleButton,
    settleLoading,
    settleOrder,
  } = useSettle(order, refetchOrder, addonFeatureManager);
  const { canEditOrder } = useEditOrder(order, authenticationManager);

  return (
    <div className={styles.orderDetailContainer}>
      {(approveLoading || rejectLoading || settleLoading) && (
        <LoadingIndicator />
      )}
      <div className={styles.orderDetail}>
        <div className={styles.topArea}>
          <div className={styles.orderName} onClick={toggleShowDetail}>
            <FontAwesomeIcon icon={showDetail ? faAngleDown : faAngleUp} />
            <div>
              {i18n.t<string>('orderDetail.labels.name', {
                name: order.projectName,
              })}
            </div>
          </div>
          <div className={styles.buttonArea}>
            <div className={styles['activation-group']}>
              {showApproveButton && (
                <PermissionChecker
                  permissionAware={hasFuncs(Permission.ORDER_APPROVE)}
                >
                  {remainingStoredValueError ? (
                    <OverlayTrigger
                      placement={'bottom'}
                      trigger={['hover', 'focus']}
                      overlay={
                        <Tooltip id={'remainingStoredValueError'}>
                          {remainingStoredValueError}
                        </Tooltip>
                      }
                    >
                      <ApproveButton disabled />
                    </OverlayTrigger>
                  ) : (
                    <ApproveButton onClick={approveOrder} />
                  )}
                </PermissionChecker>
              )}
              {showRejectButton && (
                <PermissionChecker
                  permissionAware={hasFuncs(Permission.ORDER_APPROVE)}
                >
                  <RejectButton onClick={rejectOrder} />
                </PermissionChecker>
              )}
            </div>
            {canEditOrder && (
              <Link to={`/orders/${order.orderNumber}/edit`}>
                {i18n.t<string>('orderDetail.labels.editOrder')}
              </Link>
            )}
            {showSettleButton && (
              <PermissionChecker
                permissionAware={hasFuncs(Permission.ORDER_WRITE)}
              >
                <SettleButton
                  disabled={!canSettle}
                  settleTips={settleTips}
                  onSettle={settleOrder}
                />
              </PermissionChecker>
            )}
            <Link
              to={getReportLink(order, ReportType.PERFORMANCE)}
              target='_blank'
            >
              {i18n.t<string>('orderDetail.labels.report')}
            </Link>
          </div>
        </div>
        {showDetail && (
          <div className={styles.detail}>
            <OrderDetailInfo order={order} />
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetailContent;
